import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Input, InputNumber, Modal, Pagination, Select, Spin, message } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../i18n';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import {
  createDmCampaign,
  getCommerceProducts,
  getContentTemplates,
  getInfluencerCollectionPage,
  getInfluencerPage
} from '../../../store/request';
import { SHOP_PRODUCT_PAGE_SIZE, commissionRateToPercent, type ShopProduct } from '../../../types/commerce';
import type { InfluencerCollectionRecord, InfluencerRecord } from '../../../types/influencer';
import type { TemplateListItem } from '../../../types/template';

type TalentTab = 'mine' | 'square';

type TalentSource = {
  influencerId?: string;
  creatorOpenId?: string;
  nickname?: string;
  username?: string;
  avatarUrl?: string;
  selectionRegion?: string;
  categoryNames?: string[];
  isBlacklist?: boolean | null;
  status?: number | null;
};

type PickerTalent = {
  id: string;
  name: string;
  username: string;
  avatarUrl: string;
  initial: string;
  meta: string;
};

type CreateBroadcastModalProps = {
  open: boolean;
  shopId?: string | null;
  onClose: () => void;
  onCreated: () => void;
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-sm text-slate-500">{children}</div>;
}

function productKey(item: ShopProduct, index: number, page: number) {
  return item.productId?.trim() || `product-${page}-${index}`;
}

function productTitleOf(item: ShopProduct) {
  return item.productName?.trim() || item.productId?.trim() || '—';
}

function toPickerTalent(row: TalentSource): PickerTalent | null {
  if (row.isBlacklist || row.status === 3) return null;
  const id = String(row.influencerId ?? '').trim() || String(row.creatorOpenId ?? '').trim();
  if (!id) return null;
  const username = row.username?.replace(/^@/, '').trim() || '';
  const name = row.nickname?.trim() || username || id;
  const categories = row.categoryNames?.filter(Boolean).slice(0, 2).join(' · ') ?? '';
  return {
    id,
    name,
    username,
    avatarUrl: row.avatarUrl?.trim() || '',
    initial: name.slice(0, 1).toUpperCase() || '?',
    meta: [username ? `@${username}` : '', row.selectionRegion, categories].filter(Boolean).join(' · ') || '—'
  };
}

export function CreateBroadcastModal({ open, shopId, onClose, onCreated }: CreateBroadcastModalProps) {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const { shops } = useSelectedShop();
  const [taskName, setTaskName] = useState('');
  const [tab, setTab] = useState<TalentTab>('mine');
  const [keyword, setKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [usernames, setUsernames] = useState<Record<string, string>>({});
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateId, setTemplateId] = useState<number>();
  const [bodyText, setBodyText] = useState('');
  const [showProduct, setShowProduct] = useState(false);
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productReloadKey, setProductReloadKey] = useState(0);
  const [productKeywordInput, setProductKeywordInput] = useState('');
  const [productKeyword, setProductKeyword] = useState('');
  const appliedProductKeywordRef = useRef('');
  const refreshProductsRequested = useRef(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [rates, setRates] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [mineRecords, setMineRecords] = useState<InfluencerCollectionRecord[]>([]);
  const [mineTotal, setMineTotal] = useState(0);
  const [mineLoading, setMineLoading] = useState(false);
  const [squareRecords, setSquareRecords] = useState<InfluencerRecord[]>([]);
  const [squareLoading, setSquareLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTaskName('');
    setTab('mine');
    setKeyword('');
    setSelectedIds([]);
    setUsernames({});
    setTemplates([]);
    setTemplateId(undefined);
    setBodyText('');
    setShowProduct(false);
    setProducts([]);
    setProductPage(1);
    setProductTotal(0);
    setProductKeywordInput('');
    setProductKeyword('');
    appliedProductKeywordRef.current = '';
    setSelectedProductId('');
    setRates({});
    setProductReloadKey(0);
    setSquareRecords([]);
    setSubmitting(false);
  }, [open]);

  const mineKeyword = tab === 'mine' ? keyword.trim() : '';
  const squareKeyword = tab === 'square' ? keyword.trim() : '';
  const currentShopId = shopId?.trim() || '';
  const shopName = shops.find((item) => item.shopId === currentShopId)?.shopName?.trim() || '—';
  const selectedTemplate = templates.find((item) => item.id === templateId);

  useEffect(() => {
    if (!open) return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setMineRecords([]);
      setMineTotal(0);
      setMineLoading(false);
      return;
    }
    let cancelled = false;
    setMineLoading(true);
    const timer = window.setTimeout(() => {
      const loadAll = async () => {
        const pageSize = 50;
        const all: InfluencerCollectionRecord[] = [];
        let pageNum = 1;
        let total = 0;
        while (pageNum <= 40) {
          const res = await getInfluencerCollectionPage({
            userCode,
            pageNum,
            pageSize,
            ...(mineKeyword ? { keyword: mineKeyword } : {})
          });
          if (cancelled) return;
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('creator.load_failed'));
            setMineRecords([]);
            setMineTotal(0);
            return;
          }
          const page = res.content;
          const records = page?.records;
          const list = Array.isArray(records) ? records : [];
          total = typeof page?.total === 'number' ? page.total : all.length + list.length;
          all.push(...list);
          if (list.length === 0 || all.length >= total) break;
          pageNum += 1;
        }
        if (cancelled) return;
        setMineRecords(all);
        setMineTotal(total);
      };
      loadAll()
        .catch(() => {
          if (!cancelled) {
            message.error(t('creator.load_failed'));
            setMineRecords([]);
            setMineTotal(0);
          }
        })
        .finally(() => {
          if (!cancelled) setMineLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, account?.userCode, mineKeyword, t]);

  useEffect(() => {
    if (!open || tab !== 'square') return;
    if (!squareKeyword) {
      setSquareRecords([]);
      setSquareLoading(false);
      return;
    }
    let cancelled = false;
    setSquareLoading(true);
    const timer = window.setTimeout(() => {
      getInfluencerPage({ pageNum: 1, pageSize: 50, keyword: squareKeyword })
        .then((res) => {
          if (cancelled) return;
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('talent.load_failed'));
            setSquareRecords([]);
            return;
          }
          const list = res.content?.records;
          setSquareRecords(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) message.error(t('talent.load_failed'));
        })
        .finally(() => {
          if (!cancelled) setSquareLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, tab, squareKeyword, t]);

  useEffect(() => {
    if (!open) return;
    if (!currentShopId) {
      setTemplates([]);
      setTemplatesLoading(false);
      return;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    getContentTemplates({ shopId: currentShopId, channel: 'DM', pageNum: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('script.load_failed'));
          setTemplates([]);
          return;
        }
        const list = res.content?.records;
        setTemplates(Array.isArray(list) ? list.filter((item) => item.id != null) : []);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('script.load_failed'));
          setTemplates([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentShopId, t]);

  useEffect(() => {
    setProductPage(1);
    setSelectedProductId('');
    setRates({});
    setProductKeywordInput('');
    setProductKeyword('');
    appliedProductKeywordRef.current = '';
  }, [currentShopId]);

  useEffect(() => {
    if (!open || !showProduct) return;
    if (!currentShopId) {
      setProducts([]);
      setProductTotal(0);
      setProductsLoading(false);
      return;
    }
    let cancelled = false;
    setProductsLoading(true);
    const notify = refreshProductsRequested.current;
    refreshProductsRequested.current = false;
    const productKeywordText = productKeyword.trim();
    getCommerceProducts({
      shopId: currentShopId,
      pageNum: productPage,
      pageSize: SHOP_PRODUCT_PAGE_SIZE,
      ...(productKeywordText ? { keyword: productKeywordText } : {})
    })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('invitation.product_load_failed'));
          setProducts([]);
          setProductTotal(0);
          return;
        }
        const records = res.content?.records;
        const total = res.content?.total;
        const list = Array.isArray(records) ? records : [];
        setProducts(list);
        setProductTotal(typeof total === 'number' ? total : list.length);
        setRates((prev) => {
          const next = { ...prev };
          list.forEach((item, index) => {
            const key = productKey(item, index, productPage);
            if (next[key] != null) return;
            const percent = commissionRateToPercent(item.commissionRate);
            if (percent != null) next[key] = percent;
          });
          return next;
        });
        if (notify) message.success(res.message || t('invitation.product_refreshed'));
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('invitation.product_load_failed'));
          setProducts([]);
          setProductTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, showProduct, currentShopId, productPage, productKeyword, productReloadKey, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = productKeywordInput.trim();
      if (appliedProductKeywordRef.current === next) return;
      appliedProductKeywordRef.current = next;
      setProductKeyword(next);
      setProductPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [productKeywordInput]);

  const mineTalents = useMemo(
    () => mineRecords.map(toPickerTalent).filter((item): item is PickerTalent => item != null),
    [mineRecords]
  );
  const squareTalents = useMemo(
    () => squareRecords.map(toPickerTalent).filter((item): item is PickerTalent => item != null),
    [squareRecords]
  );
  const visibleTalents = tab === 'mine' ? mineTalents : squareTalents;
  const loading = (tab === 'mine' && mineLoading) || (tab === 'square' && squareLoading);

  const toggleTalent = (talent: PickerTalent, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? (prev.includes(talent.id) ? prev : [...prev, talent.id]) : prev.filter((id) => id !== talent.id)
    );
    setUsernames((prev) => {
      if (!checked || !talent.username) return prev;
      return { ...prev, [talent.id]: talent.username };
    });
  };

  const submit = async () => {
    if (submitting) return;
    const currentShopId = shopId?.trim() || '';
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const name = taskName.trim();
    if (!name) {
      message.warning(t('message.task_name_required'));
      return;
    }
    const creators = Array.from(new Set(selectedIds.map((id) => id.trim()).filter(Boolean))).map((creatorOpenId) => {
      const username = usernames[creatorOpenId]?.trim();
      return username ? { creatorOpenId, username } : { creatorOpenId };
    });
    if (creators.length === 0) {
      message.warning(t('invitation.pick_talent'));
      return;
    }
    const content = bodyText.trim();
    if (!content) {
      message.warning(t('message.body_required'));
      return;
    }
    const product = selectedProductId.trim();
    if (showProduct) {
      if (!product || product.length > 64) {
        message.warning(product ? t('invitation.product_id_invalid') : t('invitation.pick_product'));
        return;
      }
      const rate = rates[product];
      if (typeof rate !== 'number' || !Number.isFinite(rate) || rate < 0) {
        message.warning(t('invitation.commission_required'));
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await createDmCampaign({
        shopId: currentShopId,
        name: name.slice(0, 255),
        body: content,
        msgType: 'TEXT',
        showProductCard: showProduct,
        creators,
        ...(templateId != null ? { templateId } : {}),
        ...(showProduct ? { productId: product } : {})
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('message.create_failed'));
        return;
      }
      const count = res.content?.totalRecipients ?? creators.length;
      message.success(res.message || t('message.created', { count }));
      onCreated();
      onClose();
    } catch {
      message.error(t('message.create_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const tabBtnClass = (active: boolean) =>
    active
      ? 'rounded-full border-0 bg-[#596ae8]/10 px-3 py-1 text-xs font-medium text-[#596ae8]'
      : 'cursor-pointer rounded-full border border-[#eef0f4] bg-white px-3 py-1 text-xs text-slate-600 hover:border-[#596ae8]/30';

  return (
    <Modal
      title={t('message.create_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={680}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 } }}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" className="wb-btn-primary rounded-lg" loading={submitting} onClick={submit}>
            {t('message.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 pt-1">
        <section>
          <SectionLabel>{t('message.task_name')}</SectionLabel>
          <Input
            value={taskName}
            maxLength={255}
            placeholder={t('message.task_name_placeholder')}
            onChange={(event) => setTaskName(event.target.value)}
          />
        </section>

        <section className="space-y-3">
          <p className="mb-0 text-xs leading-relaxed text-slate-400">
            {selectedIds.length === 0 ? t('invitation.none_selected') : t('message.selected_skip', { count: selectedIds.length })}
          </p>
          <Input
            allowClear
            placeholder={t('invitation.search_placeholder')}
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={tabBtnClass(tab === 'mine')} onClick={() => setTab('mine')}>
              {t('invitation.my_talents', { count: mineTalents.length })}
            </button>
            <button type="button" className={tabBtnClass(tab === 'square')} onClick={() => setTab('square')}>
              {t('invitation.search_square')}
            </button>
          </div>
          <div className="max-h-[240px] overflow-y-auto rounded-lg border border-[#eef0f4] bg-[#fafbfc] px-2 py-1">
            {loading ? (
              <div className="flex justify-center py-6">
                <Spin size="small" />
              </div>
            ) : null}
            {!loading
              ? visibleTalents.map((talent) => (
                  <label key={talent.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-white">
                    <Checkbox checked={selectedIds.includes(talent.id)} onChange={(event) => toggleTalent(talent, event.target.checked)} />
                    {talent.avatarUrl ? (
                      <img src={talent.avatarUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" />
                    ) : (
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-xs font-semibold text-white">
                        {talent.initial}
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-800">{talent.name}</span>
                      <span className="block truncate text-xs text-slate-500">{talent.meta}</span>
                    </span>
                  </label>
                ))
              : null}
            {!loading && visibleTalents.length === 0 ? (
              <p className="px-2 py-8 text-center text-sm text-slate-400">
                {tab === 'square' && !squareKeyword ? t('invitation.square_prompt') : t('invitation.no_match')}
              </p>
            ) : null}
          </div>
        </section>

        <section>
          <SectionLabel>{t('chat.script')}</SectionLabel>
          <Select
            allowClear
            className="w-full"
            placeholder={t('message.template_placeholder')}
            loading={templatesLoading}
            value={templateId}
            options={templates
              .filter((item) => item.id != null)
              .map((item) => ({ value: item.id as number, label: item.name?.trim() || String(item.id) }))}
            onChange={(value) => {
              setTemplateId(value);
              const next = templates.find((item) => item.id === value);
              setBodyText(next?.body?.trim() || '');
            }}
          />
          {!templatesLoading && currentShopId && templates.length === 0 ? (
            <p className="mb-0 mt-2 text-xs text-slate-400">{t('message.template_empty')}</p>
          ) : null}
          {selectedTemplate?.body?.trim() ? (
            <div className="mt-2 rounded-lg bg-[#f7f8fb] px-3 py-2 text-xs leading-relaxed text-slate-500">
              {selectedTemplate.body.trim()}
            </div>
          ) : null}
          <div className="mt-3">
            <SectionLabel>{t('message.send_body')}</SectionLabel>
            <Input.TextArea rows={3} value={bodyText} onChange={(event) => setBodyText(event.target.value)} />
          </div>
        </section>

        <section>
          <Checkbox checked={showProduct} onChange={(event) => setShowProduct(event.target.checked)}>
            {t('message.show_product')}
          </Checkbox>
          {showProduct ? (
            <div className="mt-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-slate-500">
                  {t('invitation.current_shop')} <span className="font-medium text-slate-700">{shopName}</span>
                  {' · '}
                  {t('invitation.rate_adjustable')}
                </span>
                <Button
                  size="small"
                  className="rounded-lg"
                  icon={<ReloadOutlined />}
                  loading={productsLoading}
                  onClick={() => {
                    if (!currentShopId) {
                      message.warning(t('invitation.invite_shop_required'));
                      return;
                    }
                    refreshProductsRequested.current = true;
                    setProductReloadKey((key) => key + 1);
                  }}
                >
                  {t('invitation.refresh')}
                </Button>
              </div>
              <Input
                allowClear
                className="mb-2 rounded-lg"
                placeholder={t('invitation.product_keyword')}
                prefix={<SearchOutlined className="text-slate-400" />}
                value={productKeywordInput}
                onChange={(event) => setProductKeywordInput(event.target.value)}
              />
              <div className="overflow-hidden rounded-lg border border-[#eef0f4] bg-white">
                {productsLoading ? (
                  <div className="flex justify-center py-6">
                    <Spin size="small" />
                  </div>
                ) : null}
                {!productsLoading && products.length === 0 ? (
                  <p className="mb-0 px-3 py-6 text-center text-sm text-slate-400">
                    {currentShopId ? t('message.product_empty') : t('invitation.invite_shop_required')}
                  </p>
                ) : null}
                {!productsLoading
                  ? products.map((item, index) => {
                      const id = productKey(item, index, productPage);
                      return (
                        <div
                          key={id}
                          className="flex flex-wrap items-center gap-3 border-b border-[#eef0f4] px-3 py-3 last:border-b-0 sm:flex-nowrap"
                        >
                          <Checkbox
                            checked={selectedProductId === id}
                            onChange={(event) => setSelectedProductId(event.target.checked ? id : '')}
                          />
                          {item.mainImageUrl?.trim() ? (
                            <img
                              src={item.mainImageUrl}
                              alt=""
                              className="size-[56px] shrink-0 rounded-lg object-cover"
                            />
                          ) : (
                            <span className="inline-flex size-[56px] shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-semibold tracking-wide text-slate-400">
                              SKU
                            </span>
                          )}
                          <div className="min-w-0 w-[340px] shrink break-words text-[13px] font-semibold leading-snug text-slate-800">
                            {productTitleOf(item)}
                          </div>
                          <div className="ml-auto flex shrink-0 items-center gap-2 pl-2 sm:pl-0">
                            <span className="text-xs text-slate-500">{t('invitation.target_commission')}</span>
                            <InputNumber
                              min={0}
                              precision={0}
                              size="small"
                              className="w-[72px]"
                              value={rates[id]}
                              onChange={(value) => {
                                if (value === null) return;
                                setRates((prev) => ({ ...prev, [id]: value }));
                              }}
                            />
                            <span className="text-xs text-slate-400">%</span>
                          </div>
                        </div>
                      );
                    })
                  : null}
                {!productsLoading && productTotal > SHOP_PRODUCT_PAGE_SIZE ? (
                  <div className="flex justify-end border-t border-[#eef0f4] px-3 py-2">
                    <Pagination
                      size="small"
                      current={productPage}
                      pageSize={SHOP_PRODUCT_PAGE_SIZE}
                      total={productTotal}
                      showSizeChanger={false}
                      onChange={(page) => setProductPage(page)}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>

        <p className="mb-0 rounded-lg bg-[#596ae8]/[0.06] px-3 py-2 text-xs text-[#3b4dd7]">{t('message.queue_hint')}</p>
      </div>
    </Modal>
  );
}
